import type { BusinessSetupChatMessage } from '../../types/chat-message'
import { MESSAGE_ROLES, MESSAGE_STATUSES } from '../../consts/chat-message'
import type { PersistedChatState } from '../types/storage'
import {
  ASSISTANT_PANE_DEFAULT_WIDTH,
  clampAssistantPaneWidth,
} from '../../../use-assistant-pane-width'

const STORAGE_PREFIX = 'business-setup-agent'

function storageKey (userId: string | number): string {
  return `${STORAGE_PREFIX}:${userId}`
}

export function loadChatState (userId: string | number): PersistedChatState | null {
  if (typeof sessionStorage === 'undefined') {
    return null
  }

  try {
    const raw = sessionStorage.getItem(storageKey(userId))
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as PersistedChatState
    if (!Array.isArray(parsed.messages)) {
      return null
    }

    return {
      messages: parsed.messages.filter((message) => message.status !== MESSAGE_STATUSES.LOADING),
      sessionId: parsed.sessionId ?? null,
      isOpen: Boolean(parsed.isOpen),
      draft: typeof parsed.draft === 'string' ? parsed.draft : '',
      paneWidth: typeof parsed.paneWidth === 'number'
        ? clampAssistantPaneWidth(parsed.paneWidth)
        : ASSISTANT_PANE_DEFAULT_WIDTH,
      appLocale: typeof parsed.appLocale === 'string' ? parsed.appLocale : undefined,
      businessId: typeof parsed.businessId === 'string' || typeof parsed.businessId === 'number'
        ? parsed.businessId
        : undefined,
    }
  } catch {
    return null
  }
}

export function saveChatState (
  userId: string | number,
  state: PersistedChatState
): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  try {
    sessionStorage.setItem(storageKey(userId), JSON.stringify(state))
  } catch {
    // Ignore quota or privacy mode errors.
  }
}

export function clearChatState (userId: string | number): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  sessionStorage.removeItem(storageKey(userId))
}

export function clearAllChatStates (): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  const keysToRemove: string[] = []
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index)
    if (key?.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key)
    }
  }

  for (const key of keysToRemove) {
    sessionStorage.removeItem(key)
  }
}
